/**
 * Comparison pages — content model.
 *
 *   /tsg-vs-youtube              TSG vs YouTube learning
 *   /tsg-vs-edtech-platforms     TSG vs standalone EdTech platforms
 *   /tsg-vs-coaching-centres     TSG vs traditional coaching centres
 *
 * ComparePage.jsx renders all three from this file. Nothing here is page
 * code: copy, tables, destinations and the accuracy rules live in one place so
 * the claims can be reviewed without reading JSX.
 *
 * ACCURACY RULES — load-bearing, do not "strengthen":
 *  · Every TSG capability named below is already described as shipped in
 *    studentIntro/studentIntroContent.js or collegeIntro/collegeIntroContent.js
 *    (verified there against this repository on 29/30-Sep-2026): Elite
 *    Technology Mastery tracks, TSG AI Tutor (explain, notes, code review,
 *    resume review, skill tests), a compiler, skill tests, Technology MCQs,
 *    project enrolment in an engineering role, GitHub repository access, mock
 *    interviews (coding, technical, HR, aptitude, time-management) with a
 *    report per round, ExiTracker (a Jira-style work-management platform, a
 *    sister product), ExiHire (AI-powered hiring, a sister product) and the
 *    college portal (student management, certificates, analytics, GitHub
 *    dashboard).
 *  · There is NO "ATS Resume Analyzer" in the product — only an AI resume
 *    REVIEW. The pages therefore say "AI resume review" and never "ATS".
 *  · ExiTracker and ExiHire are separate products (exitracker.com,
 *    exihire.com). Pages never claim a TSG Nexus project is automatically
 *    mirrored into ExiTracker.
 *  · Competitor columns describe what is TYPICAL ("varies by creator",
 *    "depends on the centre"). They never say a competitor lacks something.
 *  · No price, duration, eligibility, student count, placement or salary
 *    claim, testimonial, rating or statistic. TSG Nexus plan details live on
 *    /tsg-product-engg and are deliberately not repeated here.
 *  · No guaranteed job, interview, placement or outcome — use helps /
 *    supports / lets you.
 */

/* ── Destinations (existing routes only) ─────────────────────────── */
export const PATHS = {
  youtube: '/tsg-vs-youtube',
  edtech: '/tsg-vs-edtech-platforms',
  coaching: '/tsg-vs-coaching-centres',
  students: '/tsg-product-engg',
  colleges: '/tsg-collab',
  partnership: '/book-demo',
  contact: '/contact',
};

/** Student sign-up. Absolute on purpose — same destination the header and the
 *  student page already use. */
export const REGISTER_URL = 'https://www.theskillgenie.com/nextgen-builder/register';

/* ── Product images (existing public assets) ─────────────────────── */
export const IMAGES = {
  github: {
    src: '/student-intro/github-repo-800.webp',
    w: 800,
    h: 413,
    alt: 'A real TSG Nexus bootcamp repository on GitHub: the Code tab showing the main branch, two branches, two commits, a .gitignore and a README.md.',
    caption: 'A real project repository on GitHub',
  },
  tracker: {
    src: '/student-intro/exitracker-overview-960.webp',
    w: 960,
    h: 640,
    alt: 'The ExiTracker product overview: the delivery flow from backlog, epic, feature, user story and task through to release, with team capacity, velocity, sprint burndown and delivery forecast charts.',
    caption: 'ExiTracker product overview (sample data)',
  },
};

/* ── Plain-language glossary (shown on every page) ───────────────── */
export const GLOSSARY = {
  title: 'Key terms, in plain words',
  lead: 'A few terms come up on this page. Here is what each one means.',
  items: [
    { term: 'GitHub', def: 'A website where software teams store their code and the full history of every change. In TSG Nexus, your project lives in a repository there.' },
    { term: 'Pull request', def: 'A request to add your change to the shared project. Teammates read it first and suggest improvements.' },
    { term: 'Code review', def: 'Someone — or an AI — reads your code and points out what is unclear or fragile before it ships. TSG offers AI code review on code you write.' },
    { term: 'ExiTracker', def: 'A Jira-style work-management platform. Work is broken into epics, user stories and tasks, then tracked on boards and sprints.' },
    { term: 'AI resume review', def: 'Feedback on your resume from the TSG AI Tutor, so you know what to tighten before anyone reads it.' },
    { term: 'Mock interview', def: 'A practice interview — coding, technical, HR, aptitude or time-management — that ends with a report on how you did.' },
  ],
};

/* ── Sibling comparison links ────────────────────────────────────── */
export const SIBLINGS = {
  youtube: { path: PATHS.youtube, label: 'TSG vs YouTube', blurb: 'From watching tutorials to building products.' },
  edtech: { path: PATHS.edtech, label: 'TSG vs EdTech platforms', blurb: 'How a connected learning-to-engineering journey differs from standalone courses.' },
  coaching: { path: PATHS.coaching, label: 'TSG vs coaching centres', blurb: 'Interview preparation versus on-the-job readiness.' },
};

/* ── SEO (consumed by src/seo/routes.config.js) ──────────────────── */
export const SEO = {
  youtube: {
    title: 'TSG vs YouTube Learning | The Skill Genie',
    description:
      'Compare YouTube learning with The Skill Genie. Discover structured technical learning, hands-on engineering projects, AI code reviews, and career preparation.',
    ogTitle: 'TSG vs YouTube Learning — The Skill Genie',
    ogDescription:
      'YouTube is a great place to start. See how The Skill Genie adds structured learning, real engineering projects, AI code review and career preparation.',
  },
  edtech: {
    title: 'TSG vs EdTech Platforms | The Skill Genie',
    description:
      'Discover how The Skill Genie connects technical learning, software engineering projects, AI-powered feedback, project management, and industry readiness.',
    ogTitle: 'TSG vs EdTech Platforms — The Skill Genie',
    ogDescription:
      'More than courses: one connected journey from technical learning to engineering projects, AI feedback and industry readiness.',
  },
  coaching: {
    title: 'TSG vs Coaching Centres | The Skill Genie',
    description:
      'Explore how The Skill Genie combines structured technical learning, engineering projects, AI code reviews, mock interviews and career preparation.',
    ogTitle: 'TSG vs Coaching Centres — The Skill Genie',
    ogDescription:
      'Prepare for interviews and for the job: practice, projects, code review and mock interviews in one digital engineering ecosystem.',
  },
};

/* ── Shared CTA labels ───────────────────────────────────────────── */
const CTA = {
  students: { label: 'Explore TSG for Students', to: PATHS.students },
  nexus: { label: 'Explore TSG Nexus', to: PATHS.students },
  colleges: { label: 'Explore TSG for Colleges', to: PATHS.colleges },
  partnership: { label: 'Discuss College Partnership', to: PATHS.partnership },
  journey: { label: 'Start Your Learning Journey', to: REGISTER_URL, external: true },
  getStarted: { label: 'Get Started', to: REGISTER_URL, external: true },
};

/* ── Shared sections ─────────────────────────────────────────────── */
const NEXUS_CAPABILITIES = [
  { name: 'Learn', body: 'Technology Mastery tracks, the TSG AI Tutor, skill tests and Technology MCQs.' },
  { name: 'Build', body: 'Enrol in a real project, choose an engineering role and work in a GitHub repository.' },
  { name: 'Get feedback', body: 'AI code review on the code you write, and a report after every mock interview round.' },
  { name: 'Prepare', body: 'Coding, technical, HR, aptitude and time-management mock interviews, plus AI resume review.' },
];

const NEXUS_NOTE =
  'Plans, access and what is included are on the TSG Nexus page. Some features may depend on your plan or college programme.';

const COLLEGE_POINTS = [
  { name: 'Manage students', body: 'Add students individually or in bulk with a CSV upload that emails their credentials.' },
  { name: 'See activity', body: 'An analytics dashboard shows per-student activity across Bootcamp, Launchpad, Mock Interview and Tech Explorer.' },
  { name: 'See project work', body: 'A GitHub dashboard lists student repositories, commits and whether each is active.' },
  { name: 'Issue certificates', body: 'Certificates carry the college’s logo and signing authority.' },
];

/* ═════════════════════════════════════════════════════════════════
   PAGE 1 — TSG vs YouTube
   ═════════════════════════════════════════════════════════════════ */
const YOUTUBE = {
  key: 'youtube',
  path: PATHS.youtube,
  visual: 'ladder',
  hero: {
    eyebrow: 'TSG vs YouTube learning',
    h1: 'Stop Just Watching Code. Start Building Real Products.',
    lead:
      'YouTube is a great place to discover concepts and learn from tutorials. The Skill Genie helps you take the next step with structured learning, hands-on engineering projects, AI-powered feedback, project collaboration, and career preparation.',
    primary: CTA.students,
    secondary: { label: 'Explore TSG Nexus', anchor: 'cmp-nexus' },
    fair: 'Not either/or: many learners start on YouTube and add TSG when they want structure, projects and feedback.',
  },
  stages: [
    { id: 'learn', name: 'Learn a concept', body: 'Understand it in a structured track.' },
    { id: 'code', name: 'Write code', body: 'Practise with tests and a compiler.' },
    { id: 'build', name: 'Build a project', body: 'Take an engineering role in a real project.' },
    { id: 'review', name: 'Review and improve', body: 'Open a pull request and act on AI code review.' },
    { id: 'track', name: 'Track progress', body: 'See scores, reports and how work moves.' },
    { id: 'show', name: 'Demonstrate your skills', body: 'Explain your work in a mock interview.' },
  ],
  challenge: {
    eyebrow: 'The challenge',
    title: 'Watching teaches you what. Building teaches you how.',
    lead:
      'Tutorials are excellent for ideas. The difficulty starts when you try to turn those ideas into working software and then show someone what you can do.',
    items: [
      { title: 'The path is yours to assemble', body: 'Playlists vary by creator, so choosing what to learn next — and in what order — is up to you.' },
      { title: 'Practice is easy to skip', body: 'Following along feels like progress. Writing the code yourself, with nobody to check it, is where gaps stay hidden.' },
      { title: 'Proof of work is scattered', body: 'Projects, notes and interview practice end up in different places, with no single record of what you have actually built.' },
    ],
  },
  table: {
    eyebrow: 'Side by side',
    title: 'YouTube learning vs The Skill Genie',
    lead:
      'YouTube creators differ widely, and many offer projects, exercises and interview tips. This table describes the typical experience and where TSG puts everything in one connected place.',
    colA: 'YouTube Learning',
    colB: 'The Skill Genie',
    groups: [
      {
        name: 'Learn',
        rows: [
          { c: 'Structured learning progression', a: { tag: 'Varies by creator', text: 'Playlists and courses can be well ordered; structure depends on who made them.' }, b: { tag: 'Built in', text: 'Technology Mastery tracks take you from fundamentals to interview topics in a set order.' } },
          { c: 'Organised technology roadmaps', a: { tag: 'Self-directed', text: 'Some creators share roadmaps. You choose and sequence them yourself.' }, b: { tag: 'Built in', text: 'Tracks are laid out by technology and topic, so the next step is already decided.' } },
          { c: 'Coding practice and assessments', a: { tag: 'Varies by creator', text: 'Some channels include exercises. Many leave practice and checking to you.' }, b: { tag: 'Built in', text: 'Skill tests, a built-in compiler and coding rounds that score your answers.' } },
          { c: 'AI Tutor', a: { tag: 'Not the focus', text: 'Questions go to comments or other communities, and reply times vary.' }, b: { tag: 'Built in', text: 'The TSG AI Tutor explains concepts, keeps notes, and answers in the language you pick.' } },
        ],
      },
      {
        name: 'Build',
        rows: [
          { c: 'Hands-on engineering projects', a: { tag: 'Varies by creator', text: 'Many tutorials build a project; you follow along on your own.' }, b: { tag: 'Built in', text: 'Enrol in a TSG Nexus project and take an engineering role in it.' } },
          { c: 'Collaborative software development', a: { tag: 'Self-directed', text: 'Collaboration is something you arrange yourself.' }, b: { tag: 'Built in', text: 'Work in a shared project repository where your pull requests are reviewed.' } },
          { c: 'GitHub and source-control workflows', a: { tag: 'Varies by creator', text: 'Taught by many creators. Applying it on a real shared project is up to you.' }, b: { tag: 'Built in', text: 'Your GitHub account is invited to the official project repository.' } },
          { c: 'AI-powered code reviews', a: { tag: 'Not the focus', text: 'Feedback comes from viewers or peers, if you ask for it.' }, b: { tag: 'Built in', text: 'AI code review on the code you write: what is fragile, what is unclear.' } },
          { c: 'Project management through ExiTracker', a: { tag: 'Not the focus', text: 'Tutorials rarely cover how work is planned. You would pick your own tools.' }, b: { tag: 'Sister product', text: 'ExiTracker shows how epics, stories and tasks are planned, tracked and delivered.' } },
        ],
      },
      {
        name: 'Prove',
        rows: [
          { c: 'AI mock interviews', a: { tag: 'Varies by creator', text: 'Videos can explain interview questions. Practising and getting a report is separate.' }, b: { tag: 'Built in', text: 'Coding, technical, HR, aptitude and time-management rounds, each with a report.' } },
          { c: 'AI resume review', a: { tag: 'Varies by creator', text: 'Advice videos are plentiful. Reviewing your own resume is a separate step.' }, b: { tag: 'Built in', text: 'The AI Tutor reviews your resume and tells you what to tighten.' } },
          { c: 'Learning progress and skill visibility', a: { tag: 'Limited', text: 'Watch history shows what you watched, not what you can do.' }, b: { tag: 'Built in', text: 'Skill-test scores and interview reports show strengths and gaps.' } },
          { c: 'Evidence of practical engineering work', a: { tag: 'Self-directed', text: 'Projects you build alone can be shown, but there is no shared record of the work.' }, b: { tag: 'Built in', text: 'Commits and pull requests in the project repository, tied to your GitHub account.' } },
          { c: 'Career preparation', a: { tag: 'Varies by creator', text: 'Plenty of free resources, which you assemble yourself.' }, b: { tag: 'Built in', text: 'Mock interviews, resume review and project work together; ExiHire is the sister hiring platform.' } },
        ],
      },
    ],
    footnote:
      'Based on the typical experience of each approach. TSG capabilities as described on the TSG Nexus page; some may depend on your plan.',
  },
  journey: {
    eyebrow: 'The journey',
    title: 'From Watching Tutorials to Building Products.',
    lead:
      'Here is what one concept — REST APIs — can look like when learning, building and feedback sit in the same place.',
    steps: [
      { name: 'Learn REST APIs', where: 'TSG Nexus', body: 'Study the lesson in a Technology Mastery track. When a term does not click, ask the TSG AI Tutor.' },
      { name: 'Test what you know', where: 'TSG Nexus', body: 'Take a skill test and try small examples in the built-in compiler.' },
      { name: 'Implement an API', where: 'TSG Nexus', body: 'In your project, take an engineering role and build an endpoint for a real feature.' },
      { name: 'Manage the source code', where: 'GitHub', body: 'Commit your work to the project repository and open a pull request.' },
      { name: 'Improve the quality', where: 'TSG Nexus', body: 'Use AI code review to find unclear or fragile parts, then revise.' },
      { name: 'Explain your decisions', where: 'Mock interview', body: 'Practise explaining why you designed the API the way you did.' },
    ],
    note: 'Which steps are available depends on your TSG Nexus enrolment. ExiTracker is a separate product.',
  },
  extras: ['tracker', 'career', 'nexus'],
  tracker: {
    eyebrow: 'ExiTracker',
    title: 'See how engineering work is planned and delivered.',
    lead:
      'Writing code is only part of engineering. ExiTracker is a Jira-style work-management platform that shows how teams break a goal into epics, stories and tasks, and track it to release.',
    points: [
      { name: 'Plan', body: 'Break work into epics, features, user stories, tasks and sub-tasks.' },
      { name: 'Track', body: 'Boards, status, priorities and sprint burndown show progress.' },
      { name: 'Deliver', body: 'Milestones and releases show when work is done.' },
    ],
    image: 'tracker',
    note: 'ExiTracker is a separate TSG sister product. The numbers in this overview are illustrative sample data.',
    link: { label: 'See the full student experience', to: PATHS.students },
  },
  career: {
    eyebrow: 'Career preparation',
    title: 'Practise explaining what you built.',
    lead:
      'Employers ask how you work, not just what you know. TSG gives you ways to rehearse that conversation before it counts.',
    points: [
      { name: 'Mock interviews', body: 'Rehearse coding, technical, HR, aptitude and time-management rounds. Every round ends with a report, so the next attempt fixes what the last one exposed.' },
      { name: 'AI resume review', body: 'Get feedback on your resume, then back it up with the project work in your repository.' },
      { name: 'Progress you can see', body: 'Skill-test scores and interview reports show where you are strong and where to practise.' },
    ],
    note: 'TSG helps you prepare. It does not guarantee interviews, jobs or salaries.',
  },
  nexus: {
    eyebrow: 'TSG Nexus',
    title: 'What TSG Nexus adds after the tutorial.',
    lead:
      'TSG Nexus is the Product Engineering Program inside The Skill Genie. It turns what you learned into a project you can build, review and explain.',
    points: NEXUS_CAPABILITIES,
    note: NEXUS_NOTE,
    link: CTA.nexus,
  },
  faq: [
    { q: 'Is The Skill Genie a replacement for YouTube?', a: 'No. YouTube is a valuable place to discover ideas, and many learners keep using it. TSG is for the next step: structured learning, a real project, feedback on your code and interview practice in one place.' },
    { q: 'Can I show an employer what I built?', a: 'Your project work lives in a GitHub repository, and your commits and pull requests carry your GitHub account. What you show, and whether anyone offers an interview, is up to you and the employer.' },
    { q: 'What is ExiTracker, and do I need it?', a: 'ExiTracker is a work-management platform that shows how engineering teams plan and deliver work. It is a separate TSG sister product that helps you understand the workflow behind a project.' },
    { q: 'Does TSG guarantee a job?', a: 'No. TSG helps you build and practise real skills. Hiring decisions belong to employers.' },
  ],
  closing: {
    title: 'Keep the curiosity. Add the engineering.',
    lead: 'Take what you learned from tutorials and turn it into a project, a pull request and a story you can explain.',
    primary: CTA.journey,
    secondary: CTA.students,
  },
};

/* ═════════════════════════════════════════════════════════════════
   PAGE 2 — TSG vs EdTech platforms
   ═════════════════════════════════════════════════════════════════ */
const EDTECH = {
  key: 'edtech',
  path: PATHS.edtech,
  visual: 'ecosystem',
  hero: {
    eyebrow: 'TSG vs EdTech platforms',
    h1: 'More Than Courses. A Complete Engineering Readiness Ecosystem.',
    lead:
      'Connect technical learning, practical software development, AI-powered feedback, project collaboration, and career preparation in one structured learning journey.',
    primary: CTA.students,
    secondary: CTA.colleges,
    fair: 'Good EdTech platforms are strong at courses. TSG’s difference is how learning, building and feedback connect.',
  },
  hub: {
    center: 'Your TSG Nexus workspace',
    nodes: [
      { id: 'tutor', name: 'AI Tutor', sub: 'Explanations and notes' },
      { id: 'tracks', name: 'Technology tracks', sub: 'Structured learning' },
      { id: 'tests', name: 'Skill tests', sub: 'Practice and scores' },
      { id: 'project', name: 'Project and GitHub', sub: 'Build with a role' },
      { id: 'review', name: 'AI code review', sub: 'Feedback on your code' },
      { id: 'tracker', name: 'ExiTracker', sub: 'How work is delivered' },
      { id: 'mock', name: 'Mock interviews', sub: 'Reports per round' },
      { id: 'resume', name: 'AI resume review', sub: 'Tighten your resume' },
    ],
  },
  challenge: {
    eyebrow: 'The challenge',
    title: 'Courses teach topics. Engineering readiness needs more.',
    lead:
      'Standalone platforms can be excellent at what they do. Readiness for a software job usually spans several kinds of experience, which learners often have to stitch together.',
    items: [
      { title: 'Learning and building are separate', body: 'A course ends with a certificate. Turning it into a project, with real source control, is another task.' },
      { title: 'Feedback arrives late', body: 'Without a review of your code as you write it, weak habits can repeat for months.' },
      { title: 'Career prep sits elsewhere', body: 'Interview practice and resume work often live in other tools, away from the work you are trying to describe.' },
    ],
  },
  table: {
    eyebrow: 'Side by side',
    title: 'A typical learning platform vs The Skill Genie',
    lead:
      'Individual platforms differ, and many offer several of these features. The table shows where the typical standalone experience ends and where TSG connects the pieces.',
    colA: 'Typical Standalone Learning Platform',
    colB: 'The Skill Genie',
    groups: [
      {
        name: 'Learn',
        rows: [
          { c: 'Structured technical learning', a: { tag: 'Common', text: 'A core strength of most platforms.' }, b: { tag: 'Built in', text: 'Technology Mastery tracks, with pages that switch into 23 language options.' } },
          { c: 'Technology roadmaps', a: { tag: 'Common', text: 'Often offered as learning paths.' }, b: { tag: 'Built in', text: 'Tracks laid out by technology and topic, from fundamentals to interview material.' } },
          { c: 'AI Tutor and technical support', a: { tag: 'Varies', text: 'Increasingly offered. How closely it connects to your project varies.' }, b: { tag: 'Built in', text: 'The TSG AI Tutor explains, keeps notes, and also reviews code and resumes.' } },
          { c: 'Coding practice and assessments', a: { tag: 'Common', text: 'Widely offered, in many formats.' }, b: { tag: 'Built in', text: 'Skill tests, Technology MCQs, a built-in compiler and coding rounds.' } },
        ],
      },
      {
        name: 'Build',
        rows: [
          { c: 'Hands-on engineering projects', a: { tag: 'Varies', text: 'Offered by many. Format ranges from guided exercises to capstones.' }, b: { tag: 'Built in', text: 'Enrol in a real project and choose an engineering role.' } },
          { c: 'Product-building experience', a: { tag: 'Varies', text: 'Depends on the programme.' }, b: { tag: 'Built in', text: 'Project versions and sprint timelines tracked on your dashboard.' } },
          { c: 'Team collaboration', a: { tag: 'Varies', text: 'Often part of cohort programmes.' }, b: { tag: 'Built in', text: 'A shared repository where pull requests are reviewed.' } },
          { c: 'GitHub integration and workflows', a: { tag: 'Varies', text: 'Often taught. Access to a real shared repository depends on the programme.' }, b: { tag: 'Built in', text: 'Your GitHub account is invited to the official project repository.' } },
          { c: 'AI-powered code reviews', a: { tag: 'Varies', text: 'Offered by some platforms.' }, b: { tag: 'Built in', text: 'AI code review on code you write.' } },
          { c: 'Project management through ExiTracker', a: { tag: 'Separate tool', text: 'Usually a tool you set up yourself.' }, b: { tag: 'Sister product', text: 'ExiTracker, a Jira-style work-management platform for epics, stories and sprints.' } },
        ],
      },
      {
        name: 'Prove',
        rows: [
          { c: 'AI mock interviews', a: { tag: 'Common', text: 'Many platforms include interview practice; format varies.' }, b: { tag: 'Built in', text: 'Coding, technical, HR, aptitude and time-management rounds, each with a report.' } },
          { c: 'AI resume review', a: { tag: 'Common', text: 'A frequent add-on.' }, b: { tag: 'Built in', text: 'Resume feedback from the AI Tutor, alongside your project work.' } },
          { c: 'Skill assessment and progress visibility', a: { tag: 'Common', text: 'Scores and completion tracking are widely available.' }, b: { tag: 'Built in', text: 'Test scores and interview reports show strengths and gaps.' } },
          { c: 'College-oriented training and reporting', a: { tag: 'Varies', text: 'Offered by some institutional programmes.' }, b: { tag: 'Built in', text: 'A college portal for student management, certificates, analytics and a GitHub dashboard.' } },
          { c: 'Evidence of practical engineering work', a: { tag: 'Varies', text: 'Completion certificates are common. Project evidence varies.' }, b: { tag: 'Built in', text: 'Commits and pull requests tied to your GitHub account.' } },
        ],
      },
    ],
    footnote:
      'Based on the typical standalone platform; individual platforms may offer more. TSG capabilities as described on the TSG Nexus page; some may depend on your plan or college programme.',
  },
  journey: {
    eyebrow: 'How it connects',
    title: 'One journey, four stages.',
    lead: 'Each stage feeds the next, so what you learn becomes what you build, and what you build becomes what you can explain.',
    steps: [
      { name: 'Learn', where: 'Tracks + AI Tutor', body: 'Study a technology in a structured track. Ask the AI Tutor when something is unclear.' },
      { name: 'Build', where: 'Project + GitHub', body: 'Enrol in a project, take a role, and commit to a real repository.' },
      { name: 'Improve', where: 'AI code review', body: 'Review your own code with AI feedback, then revise before it reaches your teammates.' },
      { name: 'Demonstrate', where: 'Mock interviews', body: 'Practise explaining your work, then tighten your resume with AI resume review.' },
    ],
    note: 'ExiTracker, which shows how teams plan and track work, is a separate TSG sister product.',
  },
  extras: ['nexus', 'college'],
  nexus: {
    eyebrow: 'TSG Nexus Product Engineering Program',
    title: 'The program that connects the pieces.',
    lead:
      'TSG Nexus is where learning turns into engineering practice. You learn a technology, enrol in a project, work in a GitHub repository, and prepare to explain what you built.',
    points: NEXUS_CAPABILITIES,
    note: NEXUS_NOTE,
    link: CTA.nexus,
  },
  college: {
    eyebrow: 'For colleges',
    title: 'Support your curriculum with engineering practice.',
    lead:
      'TSG complements academic education. It does not replace it. Colleges can add structured technical learning, project work and practice, and see how students are progressing.',
    points: COLLEGE_POINTS,
    note: 'TSG does not promise placements or institutional outcomes. Reporting reflects student activity on the platform.',
    link: CTA.colleges,
    link2: CTA.partnership,
  },
  faq: [
    { q: 'How is TSG different from a course platform?', a: 'Many platforms are strong at courses. TSG puts a course-style track, a real project, code review, mock interviews and resume review in one connected journey.' },
    { q: 'Does TSG replace other learning platforms?', a: 'It does not have to. Some learners use TSG alongside other platforms. TSG’s focus is the path from learning to building and explaining.' },
    { q: 'Is there a version for colleges?', a: 'Yes. The college portal covers student management, certificates, analytics and a GitHub dashboard. See the TSG for Colleges page, or ask about a partnership.' },
    { q: 'Does TSG guarantee placements?', a: 'No. TSG helps students build and practise skills. Hiring decisions belong to employers.' },
  ],
  closing: {
    title: 'Learning is the start. Readiness is the goal.',
    lead: 'See how a connected journey from learning to engineering practice can work for you or your college.',
    primary: CTA.getStarted,
    secondary: CTA.partnership,
  },
};

/* ═════════════════════════════════════════════════════════════════
   PAGE 3 — TSG vs coaching centres
   ═════════════════════════════════════════════════════════════════ */
const COACHING = {
  key: 'coaching',
  path: PATHS.coaching,
  visual: 'path',
  hero: {
    eyebrow: 'TSG vs coaching centres',
    h1: 'Don\'t Just Prepare for Interviews. Prepare to Perform on the Job.',
    lead:
      'Technical knowledge matters. So does the ability to write code, collaborate with engineers, build software, improve through feedback, and explain your engineering decisions.',
    primary: CTA.journey,
    secondary: CTA.nexus,
    fair: 'Good coaching centres offer excellent instructors and mentoring. TSG can sit alongside them.',
  },
  stages: [
    { id: 'know', name: 'Know the concept', body: 'Learn the technology in a structured track.' },
    { id: 'code', name: 'Apply it in code', body: 'Practise with tests, a compiler and AI help.' },
    { id: 'team', name: 'Work like a team', body: 'Use GitHub, pull requests and code review.' },
    { id: 'perform', name: 'Explain and perform', body: 'Rehearse in mock interviews, backed by your project.' },
  ],
  challenge: {
    eyebrow: 'The challenge',
    title: 'Interview-ready and job-ready are not the same.',
    lead:
      'Coaching can give you strong fundamentals and interview technique. On the job, you are also expected to work in a team, use source control and respond to feedback.',
    items: [
      { title: 'Knowing is not yet doing', body: 'Explaining a concept differs from shipping a working feature as part of a team.' },
      { title: 'Feedback depends on time', body: 'A trainer’s attention is limited. The more learners in a batch, the less feedback each one gets on every piece of code.' },
      { title: 'Proof stays informal', body: 'A certificate shows you attended. A record of commits and pull requests shows what you did.' },
    ],
  },
  table: {
    eyebrow: 'Side by side',
    title: 'Traditional coaching vs The Skill Genie',
    lead:
      'Many coaching centres offer practical projects, mentoring and strong interview preparation. This table describes the typical approach and where TSG adds an on-demand digital engineering ecosystem.',
    colA: 'Traditional Coaching Approach',
    colB: 'The Skill Genie',
    groups: [
      {
        name: 'Learn',
        rows: [
          { c: 'Instructor-led explanations and guided learning', a: { tag: 'A strength', text: 'A good instructor reads the room and adapts. Live explanation is hard to match.' }, b: { tag: 'Complements', text: 'The AI Tutor explains on demand, but does not replace a human mentor.' } },
          { c: 'Self-paced technical learning', a: { tag: 'Varies', text: 'Often follows a batch schedule.' }, b: { tag: 'Built in', text: 'Technology Mastery tracks you can follow at your own pace.' } },
          { c: 'Coding practice', a: { tag: 'Common', text: 'Assignments and labs; format varies by centre.' }, b: { tag: 'Built in', text: 'A built-in compiler, skill tests and coding rounds.' } },
          { c: 'AI Tutor', a: { tag: 'Not typical', text: 'Doubts usually go to the trainer in class or in a doubt session.' }, b: { tag: 'Built in', text: 'Ask the TSG AI Tutor at any hour, in the language you pick.' } },
        ],
      },
      {
        name: 'Build',
        rows: [
          { c: 'Practical engineering projects', a: { tag: 'Varies', text: 'Many centres run capstone or live projects.' }, b: { tag: 'Built in', text: 'Enrol in a TSG Nexus project and take an engineering role.' } },
          { c: 'Collaborative software development', a: { tag: 'Varies', text: 'Strong where a batch builds one project together.' }, b: { tag: 'Built in', text: 'A shared repository where pull requests are reviewed.' } },
          { c: 'GitHub and source control', a: { tag: 'Common', text: 'Taught in many programmes.' }, b: { tag: 'Built in', text: 'Your GitHub account is invited to the official project repository.' } },
          { c: 'Code review and feedback', a: { tag: 'A strength', text: 'Trainers give personal feedback, which depends on batch size and time.' }, b: { tag: 'Built in', text: 'AI code review on the code you write, whenever you ask.' } },
          { c: 'Project tasks and milestones', a: { tag: 'Varies', text: 'Depends on how each trainer runs the project.' }, b: { tag: 'Sister product', text: 'ExiTracker shows how tasks, sprints and milestones are managed.' } },
        ],
      },
      {
        name: 'Prove',
        rows: [
          { c: 'Progress tracking and assessments', a: { tag: 'Common', text: 'Tests and trainer evaluations.' }, b: { tag: 'Built in', text: 'Skill-test scores and interview reports show strengths and gaps.' } },
          { c: 'AI mock interviews', a: { tag: 'A strength', text: 'Often a highlight, run by experienced people.' }, b: { tag: 'Built in', text: 'Coding, technical, HR, aptitude and time-management rounds, available when you are.' } },
          { c: 'AI resume review', a: { tag: 'Common', text: 'Often a personal session with a mentor.' }, b: { tag: 'Built in', text: 'Instant feedback on your resume from the AI Tutor.' } },
          { c: 'Evidence of project contributions', a: { tag: 'Varies', text: 'A certificate is common. Project proof varies by centre.' }, b: { tag: 'Built in', text: 'Commits and pull requests tied to your GitHub account.' } },
          { c: 'Ability to demonstrate practical skills', a: { tag: 'Varies', text: 'Depends on the project and how well you document it.' }, b: { tag: 'Built in', text: 'A repository and a workflow you can explain.' } },
          { c: 'College-level training visibility', a: { tag: 'Varies', text: 'Reporting depends on the institution and centre.' }, b: { tag: 'Built in', text: 'A college portal with student analytics and a GitHub dashboard.' } },
        ],
      },
    ],
    footnote:
      'Based on the typical coaching approach; many centres do more. TSG capabilities as described on the TSG Nexus page; some may depend on your plan or college programme.',
  },
  journey: {
    eyebrow: 'An engineering story',
    title: 'From concept to a conversation about your work.',
    lead: 'Eight steps show how knowledge can turn into skills you can demonstrate, using one feature as the example.',
    steps: [
      { name: 'Learn the technology', where: 'TSG Nexus', body: 'Work through the relevant Technology Mastery track.' },
      { name: 'Practise core concepts', where: 'TSG Nexus', body: 'Use skill tests, MCQs and the compiler to find what you do not know yet.' },
      { name: 'Implement part of a project', where: 'TSG Nexus', body: 'Take an engineering role and build a piece of a real project.' },
      { name: 'Manage code with GitHub', where: 'GitHub', body: 'Commit to the project repository and open a pull request.' },
      { name: 'Organise tasks and milestones', where: 'ExiTracker', body: 'Plan the work as stories and tasks, the way teams do. ExiTracker is a separate product.' },
      { name: 'Improve through review', where: 'AI code review', body: 'Act on code review feedback and revise your implementation.' },
      { name: 'Explain your decisions', where: 'Mock interview', body: 'Rehearse explaining why you built it that way.' },
      { name: 'Present your work', where: 'Your repository', body: 'Bring the project, and your own words, to a real interview.' },
    ],
    note: 'Which steps are available depends on your TSG Nexus enrolment. ExiTracker is not automatically connected to your project.',
  },
  extras: ['together', 'nexus', 'college'],
  together: {
    eyebrow: 'Better together',
    title: 'Use each for what it does best.',
    lead:
      'You do not have to choose. A coaching programme and TSG cover different parts of the journey.',
    columns: [
      { name: 'A coaching centre is strong for', items: ['Live instruction and mentoring', 'Fixed schedules that keep you accountable', 'Personal interview coaching'] },
      { name: 'TSG is strong for', items: ['Practice and answers on demand', 'A real project and source-control history', 'Feedback on your code whenever you ask'] },
    ],
  },
  nexus: {
    eyebrow: 'TSG Nexus',
    title: 'The practical engineering layer.',
    lead:
      'TSG Nexus is the Product Engineering Program inside The Skill Genie. It focuses on the part that is hardest to practise in a classroom: working on a real project and explaining it.',
    points: NEXUS_CAPABILITIES,
    note: NEXUS_NOTE,
    link: CTA.nexus,
  },
  college: {
    eyebrow: 'For colleges',
    title: 'Give faculty a view of practical progress.',
    lead:
      'TSG complements classroom teaching. Colleges can use the college portal to see how students are engaging with learning, practice and projects.',
    points: COLLEGE_POINTS,
    note: 'TSG does not promise placements or institutional outcomes. Reporting reflects student activity on the platform.',
    link: CTA.colleges,
    link2: CTA.partnership,
  },
  faq: [
    { q: 'Is TSG a coaching centre?', a: 'No. TSG is a digital platform for structured learning, engineering projects, AI feedback and interview practice. It does not replace live instructors or personal mentoring.' },
    { q: 'Can I use TSG alongside a coaching programme?', a: 'Yes. Many learners can use a coaching centre for live instruction and TSG for on-demand practice, project work and feedback on code.' },
    { q: 'How are mock interviews different?', a: 'TSG mock interviews are AI-run rounds you can take when you are ready, with a report after each one. They do not replace practice with a person.' },
    { q: 'Does TSG guarantee a job?', a: 'No. TSG helps you build and practise real skills. Hiring decisions belong to employers.' },
  ],
  closing: {
    title: 'Be ready for the interview. Be ready for the job.',
    lead: 'Build something real, get feedback on it, and learn to explain it.',
    primary: CTA.journey,
    secondary: CTA.nexus,
  },
};

export const PAGES = { youtube: YOUTUBE, edtech: EDTECH, coaching: COACHING };
